import React, { useState } from 'react';
import { 
  Box, 
  Drawer, 
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Collapse,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import { 
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Category as CategoriesIcon,
  ShoppingCart as ProductsIcon,
  LocalShipping as SuppliersIcon,
  Payments as PaymentsIcon,
  Assessment as ReportsIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  LocalOffer as PromotionsIcon,
  People as StaffIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Storefront as StoreIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import MainAdminLayout from './MainAdminLayout';

const drawerWidth = 240;

interface StoreAdminLayoutProps {
  children: React.ReactNode;
}

const StoreAdminLayout: React.FC<StoreAdminLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(!isMobile);
  const location = useLocation();

  // State for expandable menu sections
  const [productsOpen, setProductsOpen] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  // Function to check if a path is active
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // ListItemLink component for navigation links
  const ListItemLink = ({ to, primary, icon }: { to: string; primary: string; icon: React.ReactNode }) => {
    const active = isActive(to);
    
    return (
      <ListItem 
        button 
        component={Link} 
        to={to}
        sx={{
          backgroundColor: active ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          borderLeft: active ? '4px solid' : '4px solid transparent',
          borderLeftColor: active ? 'primary.main' : 'transparent',
          pl: active ? 1.75 : 2,
        }}
      >
        <ListItemIcon sx={{ color: active ? 'primary.main' : 'inherit' }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={primary} 
          primaryTypographyProps={{
            color: active ? 'primary' : 'inherit',
            fontWeight: active ? 'bold' : 'normal',
          }}
        />
      </ListItem>
    );
  };

  const storeNavigation = (
    <List component="nav">
      <ListItemLink
        to="/admin/store/dashboard"
        primary="Dashboard"
        icon={<DashboardIcon />}
      />
      
      {/* Products Section */}
      <ListItem button onClick={() => setProductsOpen(!productsOpen)}>
        <ListItemIcon>
          <ProductsIcon />
        </ListItemIcon>
        <ListItemText primary="Products" />
        {productsOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={productsOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemLink
            to="/admin/store/products/list"
            primary="All Products"
            icon={<ProductsIcon />}
          />
          <ListItemLink
            to="/admin/store/products/categories"
            primary="Categories"
            icon={<CategoriesIcon />}
          />
          <ListItemLink
            to="/admin/store/products/promotions"
            primary="Promotions"
            icon={<PromotionsIcon />}
          />
        </List>
      </Collapse>
      
      {/* Inventory Section */}
      <ListItem button onClick={() => setInventoryOpen(!inventoryOpen)}>
        <ListItemIcon>
          <InventoryIcon />
        </ListItemIcon>
        <ListItemText primary="Inventory" />
        {inventoryOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={inventoryOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemLink
            to="/admin/store/inventory/stock"
            primary="Stock Levels"
            icon={<InventoryIcon />}
          />
          <ListItemLink
            to="/admin/store/inventory/suppliers"
            primary="Suppliers"
            icon={<SuppliersIcon />}
          />
        </List>
      </Collapse>
      
      {/* Sales & Payments */}
      <ListItemLink
        to="/admin/store/sales"
        primary="Sales"
        icon={<PaymentsIcon />}
      />
      
      {/* Reports */}
      <ListItemLink
        to="/admin/store/reports"
        primary="Reports"
        icon={<ReportsIcon />}
      />
      
      {/* Settings Section */}
      <ListItem button onClick={() => setSettingsOpen(!settingsOpen)}>
        <ListItemIcon>
          <SettingsIcon />
        </ListItemIcon>
        <ListItemText primary="Settings" />
        {settingsOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemLink
            to="/admin/store/settings/general"
            primary="General"
            icon={<SettingsIcon />}
          />
          <ListItemLink
            to="/admin/store/settings/staff"
            primary="Staff"
            icon={<StaffIcon />}
          />
          <ListItemLink
            to="/admin/store/settings/transactional-email-templates"
            primary="Transactional Email Templates"
            icon={<EmailIcon />}
          />
        </List>
      </Collapse>
    </List>
  );

  return (
    <MainAdminLayout drawerContent={storeNavigation}>
      {children}
    </MainAdminLayout>
  );
};

export default StoreAdminLayout; 